'use client';

import React, { useState, useMemo } from 'react';
import {
    ArrowRightLeft,
    Building2,
    Wallet,
    TrendingUp,
    TrendingDown,
    Search,
    Filter,
    Download,
    Calendar,
    CheckCircle2,
    Clock,
    AlertCircle,
    School,
    Banknote,
    FileText,
    Send,
    ChevronRight,
    ChevronDown,
    Plus,
    XCircle,
    Eye,
    X
} from 'lucide-react';
import Link from 'next/link';
import { getSchoolName, SchoolUnit, validUnits } from '@/lib/data';

// --- Types ---
interface TransferUnit {
    id: string;
    date: string;
    fromUnit: string;
    toUnit: string;
    srcAccount: string;
    destAccount: string;
    amount: string;
    status: 'draft' | 'pending' | 'approved' | 'rejected';
    description: string;
}

interface TransferSchool {
    id: string;
    date: string;
    fromSchool: string;
    toSchool: string;
    srcAccount: string;
    destAccount: string;
    amount: string;
    status: 'draft' | 'pending' | 'approved' | 'rejected';
    description: string;
}

// --- Mock Data ---
const MOCK_ACCOUNTS = [
    { id: 'KAS-01', name: 'Kas Besar', type: 'cash' },
    { id: 'KAS-02', name: 'Kas Kecil', type: 'cash' },
    { id: 'BANK-01', name: 'Bank BCA (4521...)', type: 'bank' },
    { id: 'BANK-02', name: 'Bank Mandiri (9001...)', type: 'bank' },
];

const MOCK_UNIT_TRANSFERS: TransferUnit[] = [
    {
        id: 'TR-UNIT-001',
        date: '2026-02-04',
        fromUnit: 'paud-pekanbaru',
        toUnit: 'paud-kampar',
        srcAccount: 'Kas Besar',
        destAccount: 'Kas Besar',
        amount: 'Rp 2.500.000',
        status: 'approved',
        description: 'Bantuan operasional mendesak'
    },
    {
        id: 'TR-UNIT-002',
        date: '2026-02-05',
        fromUnit: 'paud-pekanbaru',
        toUnit: 'paud-siak',
        srcAccount: 'Bank BCA',
        destAccount: 'Kas Besar',
        amount: 'Rp 5.000.000',
        status: 'pending',
        description: 'Dana renovasi atap'
    }
];

const MOCK_SCHOOL_TRANSFERS: TransferSchool[] = [
    {
        id: 'TR-SCH-001',
        date: '2026-02-01',
        fromSchool: 'PAUD',
        toSchool: 'SD',
        srcAccount: 'Bank Mandiri',
        destAccount: 'Bank Mandiri',
        amount: 'Rp 15.000.000',
        status: 'approved',
        description: 'Subsidi silang pembangunan'
    }
];

// --- Components ---
const DetailModal = ({ isOpen, onClose, transferData }: { isOpen: boolean, onClose: () => void, transferData: any }) => {
    if (!isOpen || !transferData) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-300">
            <div className="bg-surface w-full max-w-2xl rounded-4xl border border-border-main shadow-2xl relative overflow-hidden animate-in zoom-in-95 duration-300">
                {/* Modal Header */}
                <div className="p-6 border-b border-border-main flex items-center justify-between bg-primary-soft/30">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-primary text-white rounded-xl">
                            <Eye className="w-5 h-5" />
                        </div>
                        <div>
                            <h3 className="font-bold text-lg">Detail Transfer Kas</h3>
                            <p className="text-[10px] text-muted font-bold uppercase tracking-widest">{transferData.id}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-error-soft hover:text-error rounded-xl transition-all">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Modal Body */}
                <div className="p-8 space-y-8">
                    <div className="grid grid-cols-2 gap-8">
                        <div className="space-y-1">
                            <p className="text-[10px] font-bold text-muted uppercase tracking-widest">Informasi Dasar</p>
                            <div className="space-y-3 pt-2">
                                <div className="flex justify-between text-sm">
                                    <span className="text-secondary">Tanggal</span>
                                    <span className="font-bold text-foreground">{transferData.date}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-secondary">Status</span>
                                    <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${transferData.status === 'approved' ? 'bg-success-soft text-success border-success/10' :
                                        transferData.status === 'pending' ? 'bg-warning-soft text-warning border-warning/10' :
                                            'bg-error-soft text-error border-error/10'
                                        }`}>{transferData.status}</span>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-1">
                            <p className="text-[10px] font-bold text-muted uppercase tracking-widest">Nominal Transfer</p>
                            <div className="pt-2">
                                <span className="text-3xl font-bold text-primary tabular-nums">{transferData.amount}</span>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-8 p-6 bg-background rounded-3xl border border-border-main relative">
                        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-surface border border-border-main rounded-full flex items-center justify-center text-primary z-10 shadow-sm">
                            <ArrowRightLeft className="w-5 h-5" />
                        </div>

                        <div className="space-y-4">
                            <div className="space-y-1">
                                <p className="text-[10px] font-bold text-muted uppercase tracking-widest">Dari</p>
                                <p className="text-sm font-bold text-foreground">
                                    {transferData.type === 'unit' ? getSchoolName(transferData.fromUnit as SchoolUnit) : transferData.fromSchool}
                                </p>
                            </div>
                            <div className="space-y-1">
                                <p className="text-[10px] font-bold text-muted uppercase tracking-widest">Akun Sumber</p>
                                <div className="flex items-center gap-2 text-sm text-secondary">
                                    <Wallet className="w-4 h-4 text-primary" />
                                    <span>{transferData.srcAccount}</span>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-4 text-right">
                            <div className="space-y-1">
                                <p className="text-[10px] font-bold text-muted uppercase tracking-widest">Ke</p>
                                <p className="text-sm font-bold text-foreground">
                                    {transferData.type === 'unit' ? getSchoolName(transferData.toUnit as SchoolUnit) : transferData.toSchool}
                                </p>
                            </div>
                            <div className="space-y-1 text-right">
                                <p className="text-[10px] font-bold text-muted uppercase tracking-widest">Akun Tujuan</p>
                                <div className="flex items-center gap-2 text-sm text-secondary justify-end">
                                    <span>{transferData.destAccount}</span>
                                    <Banknote className="w-4 h-4 text-success" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <p className="text-[10px] font-bold text-muted uppercase tracking-widest">Keterangan / Alasan</p>
                        <div className="p-4 bg-background border border-border-main rounded-2xl">
                            <p className="text-sm text-secondary italic leading-relaxed">
                                "{transferData.description}"
                            </p>
                        </div>
                    </div>
                </div>

                {/* Modal Footer */}
                <div className="p-6 border-t border-border-main bg-background/30 flex justify-end gap-3">
                    <button onClick={onClose} className="px-6 py-2.5 bg-surface border border-border-main rounded-xl text-xs font-bold hover:bg-primary-soft/50 transition-all uppercase tracking-widest">
                        Tutup
                    </button>
                    {transferData.status === 'pending' && (
                        <button className="px-6 py-2.5 bg-error text-white rounded-xl text-xs font-bold hover:shadow-lg transition-all uppercase tracking-widest">
                            Batalkan Transfer
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default function TransferKasPage() {
    const initialUnit = 'paud-pekanbaru';

    // Validate if necessary, though hardcoded 'paud-pekanbaru' should be valid
    // if (!validUnits.includes(initialUnit as SchoolUnit)) {
    //     notFound();
    // }

    const baseTypeCurrent = initialUnit.split('-')[0];

    // --- States ---
    const [activeTab, setActiveTab] = useState<'unit' | 'school'>('unit');
    const [activeUnit, setActiveUnit] = useState<SchoolUnit>(initialUnit as SchoolUnit);

    // Form States
    const [srcAccount, setSrcAccount] = useState('');
    const [destUnit, setDestUnit] = useState('');
    const [destSchool, setDestSchool] = useState('');
    const [destAccount, setDestAccount] = useState('');
    const [amount, setAmount] = useState('');
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [description, setDescription] = useState('');

    // Modal State
    const [selectedTransfer, setSelectedTransfer] = useState<any>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // --- Helpers ---
    const sisterUnits = useMemo(() => {
        return validUnits.filter(u => u.startsWith(baseTypeCurrent) && u.includes('-'));
    }, [baseTypeCurrent]);

    const availableSchoolTypes = ['paud', 'sd', 'smp', 'smk'];

    const getUnitsByBaseType = (type: string) => {
        return validUnits.filter(u => u.startsWith(type) && u.includes('-'));
    };

    const formatRupiah = (value: string): string => {
        const numbers = value.replace(/\D/g, '');
        return numbers.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    };

    const getStatusStyle = (status: string) => {
        switch (status) {
            case 'approved': return 'bg-success-soft text-success border-success/10';
            case 'pending': return 'bg-warning-soft text-warning border-warning/10';
            case 'rejected': return 'bg-error-soft text-error border-error/10';
            default: return 'bg-secondary-soft text-secondary border-secondary/10';
        }
    };

    return (
        <main className="p-4 md:p-10 space-y-8 max-w-7xl mx-auto w-full pb-24 animate-in fade-in duration-700">
            {/* Header */}
            <section className="bg-surface p-8 rounded-4xl border border-border-main shadow-sm relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-96 h-96 bg-primary-soft/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none opacity-50"></div>

                <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div className="space-y-3">
                        <h1 className="text-3xl md:text-4xl font-bold text-foreground tracking-tight">Transfer Kas</h1>
                        <p className="text-secondary font-medium flex items-center gap-2">
                            <ArrowRightLeft className="w-4 h-4 text-primary" />
                            Perpindahan dana antar akun kas dan rekening bank
                        </p>
                    </div>

                    <div className="flex items-center gap-3">
                        <button className="px-5 py-3 bg-surface border border-border-main rounded-2xl text-[10px] font-bold text-foreground hover:bg-primary-soft/50 transition-all flex items-center gap-2 uppercase tracking-widest shadow-sm">
                            <Download className="w-4 h-4 text-primary" /> Export Data
                        </button>
                    </div>
                </div>
            </section>

            {/* Tab Navigation */}
            <div className="bg-surface p-1.5 rounded-3xl border border-border-main shadow-sm w-fit flex gap-1">
                <button
                    onClick={() => setActiveTab('unit')}
                    className={`px-8 py-3.5 rounded-2xl text-xs font-bold uppercase tracking-widest transition-all flex items-center gap-2 ${activeTab === 'unit' ? 'bg-primary text-white shadow-lg shadow-primary/20' : 'text-muted hover:text-primary hover:bg-primary-soft/30'
                        }`}
                >
                    <Building2 className="w-4 h-4" /> Transfer Antar Unit
                </button>
                <button
                    onClick={() => setActiveTab('school')}
                    className={`px-8 py-3.5 rounded-2xl text-xs font-bold uppercase tracking-widest transition-all flex items-center gap-2 ${activeTab === 'school' ? 'bg-primary text-white shadow-lg shadow-primary/20' : 'text-muted hover:text-primary hover:bg-primary-soft/30'
                        }`}
                >
                    <School className="w-4 h-4" /> Transfer Antar Sekolah
                </button>
            </div>

            {/* Content Section */}
            <div className="grid grid-cols-1 gap-8 animate-in slide-in-from-bottom-4 duration-500">
                {/* --- TAB 1: ANTAR UNIT --- */}
                {activeTab === 'unit' && (
                    <>
                        {/* Unit Selector Sumber */}
                        <div className="bg-surface p-6 rounded-3xl border border-border-main shadow-sm flex flex-col md:flex-row items-center gap-6">
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-primary-soft text-primary rounded-xl">
                                    <Building2 className="w-6 h-6" />
                                </div>
                                <div>
                                    <p className="text-[10px] font-bold text-muted uppercase tracking-widest">Unit Sumber Aktif</p>
                                    <h2 className="text-lg font-bold">Pilih unit asal pengiriman dana</h2>
                                </div>
                            </div>
                            <div className="flex-1 max-w-sm">
                                <select
                                    className="w-full px-5 py-3.5 bg-background border border-border-main rounded-2xl text-sm font-bold focus:ring-4 focus:ring-primary/10 transition-all outline-none appearance-none cursor-pointer"
                                    value={activeUnit}
                                    onChange={(e) => setActiveUnit(e.target.value as SchoolUnit)}
                                >
                                    {sisterUnits.map(u => (
                                        <option key={u} value={u}>{getSchoolName(u as SchoolUnit)}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Form Transfer Unit */}
                        <div className="bg-surface p-8 rounded-4xl border border-border-main shadow-sm space-y-8">
                            <div className="flex items-center gap-3">
                                <div className="p-2.5 bg-primary/10 rounded-xl text-primary"><Plus className="w-5 h-5" /></div>
                                <h3 className="text-xl font-bold">Buat Transfer Baru</h3>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-bold text-muted uppercase tracking-widest ml-1">Akun Sumber</label>
                                    <select className="w-full px-4 py-3.5 bg-background border border-border-main rounded-2xl text-sm font-bold outline-none focus:ring-4 focus:ring-primary/10 transition-all appearance-none cursor-pointer">
                                        <option value="">Pilih Akun Sumber...</option>
                                        {MOCK_ACCOUNTS.map(acc => <option key={acc.id} value={acc.id}>{acc.name}</option>)}
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-bold text-muted uppercase tracking-widest ml-1">Unit Tujuan</label>
                                    <select className="w-full px-4 py-3.5 bg-background border border-border-main rounded-2xl text-sm font-bold outline-none focus:ring-4 focus:ring-primary/10 transition-all appearance-none cursor-pointer">
                                        <option value="">Pilih Unit Tujuan...</option>
                                        {sisterUnits.filter(u => u !== activeUnit).map(u => (
                                            <option key={u} value={u}>{getSchoolName(u as SchoolUnit)}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-bold text-muted uppercase tracking-widest ml-1">Akun Tujuan (Unit Penerima)</label>
                                    <select className="w-full px-4 py-3.5 bg-background border border-border-main rounded-2xl text-sm font-bold outline-none focus:ring-4 focus:ring-primary/10 transition-all appearance-none cursor-pointer">
                                        <option value="">Pilih Akun Tujuan...</option>
                                        {MOCK_ACCOUNTS.map(acc => <option key={acc.id} value={acc.id}>{acc.name}</option>)}
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-bold text-muted uppercase tracking-widest ml-1">Nominal Transfer</label>
                                    <div className="relative">
                                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm font-bold text-primary">Rp</span>
                                        <input
                                            type="text" value={amount} onChange={(e) => setAmount(formatRupiah(e.target.value))}
                                            className="w-full pl-11 pr-4 py-3.5 bg-background border border-border-main rounded-2xl text-sm font-bold focus:ring-4 focus:ring-primary/10 outline-none tabular-nums transition-all"
                                            placeholder="0"
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-bold text-muted uppercase tracking-widest ml-1">Tanggal Transfer</label>
                                    <input
                                        type="date" value={date} onChange={(e) => setDate(e.target.value)}
                                        className="w-full px-4 py-3.5 bg-background border border-border-main rounded-2xl text-sm font-bold focus:ring-4 focus:ring-primary/10 outline-none transition-all"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-bold text-muted uppercase tracking-widest ml-1">Keterangan</label>
                                    <input
                                        type="text" placeholder="Catatan transfer..."
                                        className="w-full px-4 py-3.5 bg-background border border-border-main rounded-2xl text-sm font-bold focus:ring-4 focus:ring-primary/10 outline-none transition-all"
                                    />
                                </div>
                            </div>
                            <button className="px-10 py-4 gradient-green text-white rounded-2xl text-xs font-bold uppercase tracking-widest hover:shadow-xl hover:shadow-primary/20 transition-all flex items-center justify-center gap-2 w-full lg:w-auto">
                                <Send className="w-4 h-4" /> Proses Transfer
                            </button>
                        </div>

                        {/* Riwayat Unit */}
                        <div className="bg-surface rounded-4xl border border-border-main shadow-sm overflow-hidden">
                            <div className="p-7 border-b border-border-main flex flex-col md:flex-row md:items-center justify-between gap-4 bg-background/30">
                                <h3 className="font-bold text-xl flex items-center gap-3">
                                    <FileText className="w-5 h-5 text-primary" /> Riwayat Transfer Antar Unit
                                </h3>
                                <div className="relative w-full md:w-80">
                                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
                                    <input type="text" placeholder="Cari transaksi..." className="w-full pl-12 pr-4 py-3 bg-background border border-border-main rounded-2xl text-xs outline-none focus:ring-2 focus:ring-primary/20 transition-all" />
                                </div>
                            </div>
                            <div className="overflow-x-auto text-sm font-medium">
                                <table className="w-full text-left">
                                    <thead className="bg-background/50 border-b border-border-main">
                                        <tr>
                                            <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest">Waktu</th>
                                            <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest">Dari Unit</th>
                                            <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest">Ke Unit</th>
                                            <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest text-right">Nominal</th>
                                            <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest text-center">Status</th>
                                            <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest text-center">Aksi</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-border-main">
                                        {MOCK_UNIT_TRANSFERS.map((t) => (
                                            <tr key={t.id} className="hover:bg-primary-soft/10 transition-colors">
                                                <td className="px-6 py-5">
                                                    <div className="flex flex-col">
                                                        <span className="font-bold text-foreground">{t.date}</span>
                                                        <span className="text-[10px] text-muted">{t.id}</span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-5"><span className="text-secondary">{getSchoolName(t.fromUnit as SchoolUnit)}</span></td>
                                                <td className="px-6 py-5"><span className="text-foreground font-bold">{getSchoolName(t.toUnit as SchoolUnit)}</span></td>
                                                <td className="px-6 py-5 text-right font-bold tabular-nums text-primary">{t.amount}</td>
                                                <td className="px-6 py-5 text-center">
                                                    <span className={`px-4 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${getStatusStyle(t.status)}`}>
                                                        {t.status}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-5 text-center">
                                                    <button
                                                        onClick={() => {
                                                            setSelectedTransfer({ ...t, type: 'unit' });
                                                            setIsModalOpen(true);
                                                        }}
                                                        className="p-2 hover:bg-primary/10 rounded-xl text-primary transition-all"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}

                {/* --- TAB 2: ANTAR SEKOLAH --- */}
                {activeTab === 'school' && (
                    <>
                        <div className="bg-surface p-8 rounded-4xl border border-border-main shadow-sm flex items-center justify-between">
                            <div className="flex items-center gap-5">
                                <div className="w-14 h-14 bg-success-soft text-success rounded-2xl flex items-center justify-center">
                                    <School className="w-7 h-7" />
                                </div>
                                <div>
                                    <p className="text-[10px] font-bold text-muted uppercase tracking-widest">Sekolah Sumber</p>
                                    <h2 className="text-2xl font-bold">{getSchoolName(initialUnit as SchoolUnit)}</h2>
                                </div>
                            </div>
                        </div>

                        <div className="bg-surface p-8 rounded-4xl border border-border-main shadow-sm space-y-8">
                            <div className="flex items-center gap-3">
                                <div className="p-2.5 bg-primary/10 rounded-xl text-primary"><Send className="w-5 h-5" /></div>
                                <h3 className="text-xl font-bold">Form Transfer Antar Sekolah</h3>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-bold text-muted uppercase tracking-widest ml-1">Akun Sumber (DARI)</label>
                                    <select className="w-full px-4 py-3.5 bg-background border border-border-main rounded-2xl text-sm font-bold outline-none appearance-none">
                                        {MOCK_ACCOUNTS.map(acc => <option key={acc.id} value={acc.id}>{acc.name}</option>)}
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-bold text-muted uppercase tracking-widest ml-1">Sekolah Tujuan</label>
                                    <select
                                        className="w-full px-4 py-3.5 bg-background border border-border-main rounded-2xl text-sm font-bold outline-none appearance-none"
                                        value={destSchool}
                                        onChange={(e) => { setDestSchool(e.target.value); setDestUnit(''); }}
                                    >
                                        <option value="">Pilih Sekolah...</option>
                                        {availableSchoolTypes.map(t => <option key={t} value={t}>{t.toUpperCase()}</option>)}
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-bold text-muted uppercase tracking-widest ml-1">Unit Tujuan</label>
                                    <select
                                        className="w-full px-4 py-3.5 bg-background border border-border-main rounded-2xl text-sm font-bold outline-none appearance-none disabled:opacity-50"
                                        disabled={!destSchool}
                                    >
                                        <option value="">Pilih Unit...</option>
                                        {destSchool && getUnitsByBaseType(destSchool).map(u => (
                                            <option key={u} value={u}>{getSchoolName(u as SchoolUnit)}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-bold text-muted uppercase tracking-widest ml-1">Akun Tujuan (KE)</label>
                                    <select className="w-full px-4 py-3.5 bg-background border border-border-main rounded-2xl text-sm font-bold outline-none appearance-none">
                                        {MOCK_ACCOUNTS.map(acc => <option key={acc.id} value={acc.id}>{acc.name}</option>)}
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-bold text-muted uppercase tracking-widest ml-1">Nominal</label>
                                    <div className="relative">
                                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm font-bold text-primary">Rp</span>
                                        <input type="text" className="w-full pl-11 pr-4 py-3.5 bg-background border border-border-main rounded-2xl text-sm font-bold" />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-bold text-muted uppercase tracking-widest ml-1">Tanggal</label>
                                    <input type="date" className="w-full px-4 py-3.5 bg-background border border-border-main rounded-2xl text-sm font-bold" />
                                </div>
                                <div className="md:col-span-2 space-y-2">
                                    <label className="text-[10px] font-bold text-muted uppercase tracking-widest ml-1">Keterangan Transfer</label>
                                    <textarea rows={1} className="w-full px-4 py-3.5 bg-background border border-border-main rounded-2xl text-sm font-bold resize-none" placeholder="Alasan transfer antar sekolah..."></textarea>
                                </div>
                            </div>
                            <button className="px-12 py-4 gradient-indigo text-white rounded-2xl text-xs font-bold uppercase tracking-widest hover:shadow-xl transition-all flex items-center gap-2">
                                <ArrowRightLeft className="w-4 h-4" /> Kirim ke Sekolah Lain
                            </button>
                        </div>

                        {/* Riwayat Sekolah */}
                        <div className="bg-surface rounded-4xl border border-border-main shadow-sm overflow-hidden">
                            <div className="p-7 border-b border-border-main flex items-center justify-between">
                                <h3 className="font-bold text-xl flex items-center gap-3"><ArrowRightLeft className="w-6 h-6 text-primary" /> Riwayat Transfer Antar Sekolah</h3>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left">
                                    <thead className="bg-background/30 text-[10px] font-bold text-muted uppercase tracking-widest">
                                        <tr>
                                            <th className="px-6 py-5">Tanggal</th>
                                            <th className="px-6 py-5">Sekolah Sumber</th>
                                            <th className="px-6 py-5">Sekolah Tujuan</th>
                                            <th className="px-6 py-5">Akun Sumber</th>
                                            <th className="px-6 py-5">Akun Tujuan</th>
                                            <th className="px-6 py-5 text-right">Nominal</th>
                                            <th className="px-6 py-5 text-center">Status</th>
                                            <th className="px-6 py-5 text-center">Aksi</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-border-main">
                                        {MOCK_SCHOOL_TRANSFERS.map(t => (
                                            <tr key={t.id} className="hover:bg-primary-soft/5 transition-colors">
                                                <td className="px-6 py-5 text-sm font-bold">{t.date}</td>
                                                <td className="px-6 py-5"><span className="text-secondary">{t.fromSchool}</span></td>
                                                <td className="px-6 py-5"><span className="font-bold">{t.toSchool}</span></td>
                                                <td className="px-6 py-5 text-xs text-muted">{t.srcAccount}</td>
                                                <td className="px-6 py-5 text-xs text-muted">{t.destAccount}</td>
                                                <td className="px-6 py-5 text-right font-bold text-primary">{t.amount}</td>
                                                <td className="px-6 py-5 text-center">
                                                    <span className={`px-4 py-1.5 rounded-full text-[10px] font-bold border ${getStatusStyle(t.status)}`}>{t.status}</span>
                                                </td>
                                                <td className="px-6 py-5 text-center">
                                                    <button
                                                        onClick={() => {
                                                            setSelectedTransfer({ ...t, type: 'school' });
                                                            setIsModalOpen(true);
                                                        }}
                                                        className="p-2 hover:bg-primary/10 rounded-xl text-primary transition-all"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}
            </div>
            <DetailModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                transferData={selectedTransfer}
            />
        </main>
    );
}
